import { useState } from 'react'
import { formatDayTime } from '@dorixona/utils'
import { Icon } from '../../ui/Icon'
import { Failed, Loading, failureText } from '../../ui/Load'
import { Select } from '../../ui/Select'
import { usePage, useSavePages, type PageLangText } from './api'
import {
  LANG_NAME,
  PAGE_LANGS,
  PAGE_NAME,
  PAGE_SLUGS,
  changedEdits,
  editKey,
  isEmpty,
  problemOf,
  type PageEdit,
  type PageText,
  type PageTextsState,
} from './pages'
import { RichText } from './RichText'

export interface PageTextsProps {
  state: PageTextsState
  canUpdate: boolean
  onToast: (message: string) => void
}

const PAGE_OPTIONS = PAGE_SLUGS.map((slug) => ({ value: slug, label: PAGE_NAME[slug] }))

const isChanged = (edit: PageEdit | undefined) =>
  !!edit && (edit.title !== edit.base.title || edit.body !== edit.base.body)

/**
 * Вкладка «Тексты страниц»: политика конфиденциальности и пользовательское
 * соглашение на трёх языках. Текст пишется в редакторе с форматированием и
 * уходит на сервер в HTML — так его показывает витрина.
 *
 * Правки копятся в черновике экрана по странице и языку и уходят одной
 * кнопкой «Сохранить»: по запросу на страницу, только изменённые языки.
 */
export function PageTexts({ state, canUpdate, onToast }: PageTextsProps) {
  const { slug, lang, draft, setDraft } = state
  const page = usePage(slug)
  const save = useSavePages()
  // Ошибки полей показываем после первой попытки сохранить, а не пока пишут.
  const [checked, setChecked] = useState(false)

  const changes = changedEdits(draft)
  const dirty = changes.length

  function saveAll() {
    const bad = changes.find((edit) => problemOf(edit))
    if (bad) {
      const problem = problemOf(bad) as { title?: string; body?: string }
      setChecked(true)
      // Ошибка может быть на другой странице или языке — открываем их.
      state.setSlug(bad.slug)
      state.setLang(bad.lang)
      onToast(`${PAGE_NAME[bad.slug]}, ${LANG_NAME[bad.lang]}: ${problem.title ?? problem.body}`)
      return
    }
    save.mutate(changes, {
      onSuccess: () => {
        setChecked(false)
        setDraft({})
        onToast('Тексты страниц сохранены')
      },
      onError: (error) => {
        onToast(failureText(error))
      },
    })
  }

  const saved = page.data?.[lang]

  return (
    <>
      <section className="ui-card" aria-labelledby="st-title">
        <div className="ui-card-head">
          <h3 id="st-title">
            Тексты страниц
            {canUpdate ? null : <span className="ui-tag">только просмотр</span>}
          </h3>
          {saved && !isEmpty(saved) ? (
            <span className="aside">Обновлено {formatDayTime(saved.updatedAt)}</span>
          ) : null}
        </div>
        <div className="ui-card-body ui-form">
          <div className="st-pick">
            <Select label="Страница" value={slug} options={PAGE_OPTIONS} onChange={state.setSlug} />
            <div className="ui-seg ui-langs" role="group" aria-label="Язык страницы">
              {PAGE_LANGS.map((value) => {
                const edit = draft[editKey(slug, value)]
                const text = edit ?? page.data?.[value]
                return (
                  <button
                    key={value}
                    type="button"
                    aria-pressed={lang === value}
                    onClick={() => {
                      state.setLang(value)
                    }}
                  >
                    {LANG_NAME[value]}
                    {text && isEmpty(text) ? <span className="ui-count">Не заполнено</span> : null}
                    {isChanged(edit) ? (
                      <span className="ui-dirty-dot" aria-label="есть несохранённые изменения" />
                    ) : null}
                  </button>
                )
              })}
            </div>
          </div>
          {page.isPending ? <Loading what="текст страницы" /> : null}
          {page.isError ? (
            <Failed
              what="текст страницы"
              error={page.error}
              onRetry={() => {
                void page.refetch()
              }}
            />
          ) : null}
          {saved ? (
            <PageFields
              // Новый язык, сброс правок или новый текст с сервера — новый
              // редактор: его история правок не должна уводить в чужой текст.
              key={`${slug}-${lang}-${state.version}-${saved.updatedAt}`}
              saved={saved}
              edit={draft[editKey(slug, lang)]}
              lang={lang}
              readOnly={!canUpdate}
              checked={checked}
              onChange={(patch) => {
                const key = editKey(slug, lang)
                setDraft((prev) => {
                  const base = { title: saved.title, body: saved.body }
                  const current = prev[key] ?? { slug, lang, ...base, base }
                  return { ...prev, [key]: { ...current, ...patch } }
                })
              }}
            />
          ) : null}
        </div>
      </section>

      {canUpdate ? (
        <div className="ui-savebar" data-dirty={dirty ? true : undefined}>
          <span className="state" role="status">
            {dirty ? `Не сохранено изменений: ${dirty}` : 'Все изменения сохранены'}
          </span>
          {dirty ? (
            <button
              type="button"
              className="ui-btn"
              data-variant="secondary"
              disabled={save.isPending}
              onClick={() => {
                setChecked(false)
                state.discard()
              }}
            >
              Отменить
            </button>
          ) : null}
          <button
            type="button"
            className="ui-btn"
            disabled={!dirty || save.isPending}
            aria-busy={save.isPending}
            onClick={saveAll}
          >
            Сохранить
          </button>
        </div>
      ) : null}
    </>
  )
}

interface PageFieldsProps {
  saved: PageLangText
  edit: PageEdit | undefined
  lang: string
  readOnly: boolean
  checked: boolean
  onChange: (patch: Partial<PageText>) => void
}

/** Заголовок и текст одного языка. */
function PageFields({ saved, edit, lang, readOnly, checked, onChange }: PageFieldsProps) {
  const text = edit ?? saved
  const problem = checked && isChanged(edit) ? problemOf(text) : null

  return (
    <>
      <div className="ui-field">
        <label className="ui-label" htmlFor="pg-title">
          Заголовок
        </label>
        <input
          id="pg-title"
          placeholder={lang === 'uz' ? 'Sahifa sarlavhasi' : 'Заголовок страницы'}
          className="ui-input"
          lang={lang}
          readOnly={readOnly}
          aria-invalid={problem?.title ? true : undefined}
          aria-describedby={problem?.title ? 'pg-title-error' : undefined}
          value={text.title}
          onChange={(e) => {
            onChange({ title: e.target.value })
          }}
        />
        {problem?.title ? (
          <span className="ui-error" id="pg-title-error" role="alert">
            <Icon name="i-alert" />
            {problem.title}
          </span>
        ) : null}
      </div>
      <div className="ui-field">
        <span className="ui-label" id="pg-body-label">
          Текст
        </span>
        <RichText
          html={text.body}
          labelledBy="pg-body-label"
          lang={lang}
          readOnly={readOnly}
          invalid={!!problem?.body}
          describedBy={problem?.body ? 'pg-body-error' : undefined}
          onChange={(body) => {
            onChange({ body })
          }}
        />
        {problem?.body ? (
          <span className="ui-error" id="pg-body-error" role="alert">
            <Icon name="i-alert" />
            {problem.body}
          </span>
        ) : null}
      </div>
    </>
  )
}
